package org.catharsis.spendtel.feature.presentation.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.Psychology
import androidx.compose.material.icons.outlined.Timeline
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors

@Composable
fun AuthHeroHeader(
    isRegisterMode: Boolean,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(bottomStart = 32.dp, bottomEnd = 32.dp))
            .background(SpendTelGradients.hero)
            .padding(horizontal = 24.dp, vertical = 36.dp),
    ) {
        Box(
            modifier = Modifier
                .size(100.dp)
                .align(Alignment.TopEnd)
                .offset(x = 20.dp, y = (-10).dp)
                .clip(CircleShape)
                .background(Color.White.copy(alpha = 0.08f)),
        )
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            Text(
                text = "SPENDTEL",
                style = MaterialTheme.typography.labelMedium,
                color = Color.White.copy(alpha = 0.72f),
                letterSpacing = 2.sp,
            )
            Text(
                text = if (isRegisterMode) {
                    "Start your spending story"
                } else {
                    "Welcome back"
                },
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                lineHeight = 34.sp,
            )
            Text(
                text = "Understand your money as a story — not a spreadsheet.",
                style = MaterialTheme.typography.bodyLarge,
                color = Color.White.copy(alpha = 0.9f),
                lineHeight = 24.sp,
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                AuthValueChip(
                    icon = Icons.Outlined.AutoAwesome,
                    label = "AI stories",
                    modifier = Modifier.weight(1f),
                )
                AuthValueChip(
                    icon = Icons.Outlined.Psychology,
                    label = "Personality",
                    modifier = Modifier.weight(1f),
                )
                AuthValueChip(
                    icon = Icons.Outlined.Timeline,
                    label = "Evolution",
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

@Composable
private fun AuthValueChip(
    icon: ImageVector,
    label: String,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(14.dp),
        color = Color.White.copy(alpha = 0.12f),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 10.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(18.dp),
            )
            Text(
                text = label,
                style = MaterialTheme.typography.labelSmall,
                color = Color.White.copy(alpha = 0.9f),
                fontWeight = FontWeight.Medium,
            )
        }
    }
}

@Composable
fun AuthModeToggle(
    isRegisterMode: Boolean,
    onSignInSelected: () -> Unit,
    onRegisterSelected: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = SpendTelReportColors.canvasDark,
    ) {
        Row(modifier = Modifier.padding(4.dp)) {
            AuthModeTab(
                text = "Sign in",
                selected = !isRegisterMode,
                onClick = onSignInSelected,
                modifier = Modifier.weight(1f),
            )
            AuthModeTab(
                text = "Create account",
                selected = isRegisterMode,
                onClick = onRegisterSelected,
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun AuthModeTab(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        color = if (selected) Color.White else Color.Transparent,
        shadowElevation = if (selected) 2.dp else 0.dp,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = text,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = if (selected) FontWeight.Bold else FontWeight.Medium,
                color = if (selected) SpendTelReportColors.ink else SpendTelReportColors.inkMuted,
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
fun AuthFormCard(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(28.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 22.dp, vertical = 24.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
            content = { content() },
        )
    }
}

@Composable
fun AuthMessageBanner(
    message: String,
    isError: Boolean,
    modifier: Modifier = Modifier,
) {
    val accent = if (isError) SpendTelReportColors.increase else SpendTelReportColors.mint
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        color = accent.copy(alpha = 0.1f),
    ) {
        Text(
            text = message,
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 12.dp),
            style = MaterialTheme.typography.bodySmall,
            color = if (isError) accent else accent.darkenForText(),
        )
    }
}

private fun Color.darkenForText(): Color {
    return Color(red = red * 0.72f, green = green * 0.72f, blue = blue * 0.72f, alpha = alpha)
}

@Composable
fun authFieldColors() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = SpendTelReportColors.violet,
    unfocusedBorderColor = SpendTelReportColors.canvasDark,
    focusedLabelColor = SpendTelReportColors.violet,
    cursorColor = SpendTelReportColors.violet,
    focusedContainerColor = SpendTelReportColors.canvas.copy(alpha = 0.5f),
    unfocusedContainerColor = SpendTelReportColors.canvas.copy(alpha = 0.35f),
)

@Composable
fun webAuthFieldColors() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = SpendTelReportColors.violet,
    unfocusedBorderColor = Color(0xFFE2E8F0),
    focusedLabelColor = SpendTelReportColors.inkMuted,
    unfocusedLabelColor = SpendTelReportColors.inkMuted,
    cursorColor = SpendTelReportColors.violet,
    focusedContainerColor = Color.White,
    unfocusedContainerColor = Color(0xFFF8FAFC),
)

@Composable
fun AuthLoadingOverlay() {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.25f)),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            color = SpendTelReportColors.violet,
            strokeWidth = 3.dp,
            modifier = Modifier.size(44.dp),
        )
    }
}
